<template>
  <div :id="getId">
    <RAFTFullScreenContent :header-text="getMaterialTypeLabel">
      <h3>{{ getMaterialTypeLabel }}</h3>
      <div
        :class="{ 'table-ready': getUpdatingMaterialsStatus === 'success' }"
      />
      <BaseTable
        :items="getBioMaterials"
        :fields="getFields"
        :total-items="getTotalMaterials"
        :current-page="getCurrentPage"
        :is-busy="getBusy"
        :class-prefix="getId"
        @update-current-page="updateCurrentPage"
        ref="table"
      >
        <template #empty>
          <div class="biologicals-empty-text">
            There are currently no '{{ getMaterialTypeLabel }}' biological
            materials to list.
          </div>
        </template>
        <template #internal-link="data">
          <a :href="data.data.value.url + '?' + getDestinationQuery">{{
            data.data.value.text
          }}</a>
          <BioMaterialDataWindow
            :bio-uuid="data.data.item.id"
            :material-type="formatBioMaterialType(data.data.item.type)"
            :material-type-label="
              formatBioMaterialType(data.data.item.type, true)
            "
            :group-nid="groupNid"
          />
          <div
            v-if="data.data.item.parent_reference === null"
            class="bio-material-unlinked dog-ear"
          >
            <!-- -->
          </div>
        </template>
        <template #edit-link="data">
          <a :href="data.data.value.url + '?' + getDestinationQuery">{{
            data.data.value.text
          }}</a>
          <div
            v-if="data.data.item.parent_reference === null"
            class="bio-material-unlinked dog-ear"
          ></div>
        </template>
        <template #remove-modal="data">
          <b-modal
            size="xl"
            scrollable
            :id="getInternalId(data.data.item)"
            :busy="getBusy"
            ok-title="Confirm"
            centered
            @ok="removeMaterial(data.data.value.id)"
            @cancel="confirmBusy = false"
            @close="confirmBusy = false"
            :title="
              'Please confirm that you wish to remove this ' +
              getMaterialTypeLabel
            "
          >
            <p>{{ getMaterialTypeLabel }}: {{ data.data.item.name }}</p>
          </b-modal>
          <a @click.prevent="removeGroupMaterial(data.data.item)" href="#"
            >Remove</a
          >
        </template>
      </BaseTable>
      <SearchAddFromLibraryModal
        v-if="displayAddMaterialLinkAfterUserAccessCheck"
        :group-nid="groupNid"
        :material-type="materialType"
        @materialAdded="newMaterialAdded"
        :material-type-label="getMaterialTypeLabel"
        :url-search-params="getUrlSearchParams"
      />
    </RAFTFullScreenContent>
  </div>
</template>

<script>
import { usePermissions } from "@/composables/usePermissions";
import { mapGetters } from "vuex";
import BiologicalsListingStore from "@/store/modules/biologicals_listing";
import BaseTable from "@/components/raft/BaseTable.vue";
import RAFTFullScreenContent from "@/components/RAFTFullScreenContent.vue";
import SearchAddFromLibraryModal from "@/components/biologicals/SearchAddFromLibraryModal.vue";
import BioMaterialDataWindow from "@/components/biologicals/BioMaterialDataWindow.vue";

export default {
  name: "GroupContextBiologicalsListing",
  setup(props) {
    const {
      getPermissions,
      hasPermissions,
      storedPermissions,
      permsInContext,
      resetStoreForTesting,
    } = usePermissions(props.groupNid);
    return {
      getPermissions,
      hasPermissions,
      storedPermissions,
      permsInContext,
      resetStoreForTesting,
    };
  },
  components: {
    SearchAddFromLibraryModal,
    BaseTable,
    RAFTFullScreenContent,
    BioMaterialDataWindow,
  },
  data() {
    return {
      desiredPermissions: [
        "edit bio_material entities",
        "add bio_material entities",
      ],
    };
  },
  props: {
    materialType: { type: String, required: true },
    groupNid: { type: String, required: true },
    displayId: { type: String, default: "listing_table" },
  },
  inject: ["isB4"],
  computed: {
    ...mapGetters(["getDestinationQuery"]),
    getMaterialTypeLabel() {
      return this.$get("getMaterialTypeLabel") || this.materialType;
    },
    getBioMaterials() {
      return this.$get("getBioMaterials");
    },
    getFields() {
      const fields = this.$get("getFields");
      if (fields.length) {
        fields[0].tdAttr = (value, key, item) => {
          if (item.parent_reference === null) {
            return {
              title: "Not referenced in library",
            };
          }
          return {};
        };
        fields[0].tdClass = (value, key, item) => {
          if (item.parent_reference === null) {
            return "bio-material-unlinked";
          }
          return "";
        };
      }
      return fields;
    },
    getTotalMaterials() {
      return this.$get("getTotalMaterials");
    },
    getCurrentPage() {
      return this.$get("getCurrentPage");
    },
    getBusy() {
      return this.$get("getBusy");
    },
    getUpdatingMaterialsStatus() {
      return this.$get("getUpdatingMaterialsStatus");
    },
    getModuleName() {
      return "BiologicalsListingStore-" + this.materialType;
    },
    getId() {
      return "group-biologicals-listing-" + this.materialType;
    },
    getUrlSearchParams() {
      if (this.$route?.query) {
        return new URLSearchParams(this.$route.query);
      }
      return new URLSearchParams(window.location.search);
    },
    displayAddMaterialLinkAfterUserAccessCheck() {
      if (!this.displayAddMaterialLink) {
        return this.hasPermissions(this.desiredPermissions);
      }
      return this.displayAddMaterialLink;
    },
  },
  methods: {
    getInternalId(item) {
      const id =
        "remove-group-material-" +
        item.drupal_internal__id +
        "-" +
        item.drupal_internal__vid;
      return id;
    },
    removeGroupMaterial(item) {
      this.$bvModal.show(this.getInternalId(item));
    },
    initializeListings(data) {
      this.$dispatch("initializeListings", data);
    },
    updateCurrentPage(newPage) {
      this.$dispatch("updateCurrentPage", newPage);
    },
    removeMaterial(id) {
      this.$dispatch("removeMaterial", id);
    },
    setDefaultFilters() {
      this.$dispatch("setDefaultFilters");
    },
    updateBioMaterialsAsync() {
      this.$dispatch("updateBioMaterialsAsync");
    },
    newMaterialAdded() {
      this.setDefaultFilters();
      this.updateBioMaterialsAsync();
    },
    formatBioMaterialType(biomaterial, isLabel) {
      // Removes "bio_material--" from the beginning of the biomaterial type
      let name = biomaterial.replace(/^bio_material--/, "");

      // Take the last part after splitting by "--"
      name = name.split("--").pop();

      // If isLabel is true, replace "_" with space and capitalize the first letter
      if (isLabel) {
        name = name.replace(/_/g, " ");
        name = name.charAt(0).toUpperCase() + name.slice(1);
      }
      return name;
    },
  },
  async created() {
    const store = this.$store;
    const namespace = this.getModuleName;
    this.$store.registerModule(namespace, BiologicalsListingStore());
    this.$get = (prop) => store.getters[namespace + "/" + prop];
    this.$dispatch = (action, payload) =>
      store.dispatch(namespace + "/" + action, payload);

    const refs = this.$refs;
    this.initializeListings({
      materialType: this.materialType,
      displayId: this.displayId,
      refs: refs,
      inLibrary: 0,
      groupNid: Number(this.groupNid),
    });
    if (!this.displayAddMaterialLink) {
      await this.getPermissions(this.desiredPermissions);
    }
  },
  beforeDestroy() {
    this.$store.unregisterModule(this.getModuleName);
  },
};
</script>

<style lang="css" scoped src="../../css/B3BaseTable.css"></style>
<style lang="css" scoped src="../../css/B3Pagination.css"></style>
